/**
 * System Font Catalog — loader and default mappings
 *
 * Loads the font catalog from the JSON config file and provides
 * default per-platform mappings for the 4 schema fonts.
 *
 * The JSON catalog is editable without code changes.
 *
 * @module font-config/data
 */

import type {
  PlatformId,
  FontCategory,
  SystemFontCatalog,
  SystemFontEntry,
  ProjectFontConfig,
} from '../types/fontConfig.types';
import catalogData from './systemFontCatalog.json';
import { getWebFont, isWebFontId, webFontFallbackFamily } from './googleFontCatalog';

/**
 * Static system font catalog loaded from JSON config.
 * Contains known system fonts per platform, grouped by category.
 */
export const SYSTEM_FONT_CATALOG: SystemFontCatalog = catalogData as SystemFontCatalog;

/**
 * Default platform mappings for each schema font.
 *
 * These mappings produce the same output as the current hardcoded
 * FONT_FALLBACK_CHAINS when no user overrides exist.
 */
export const DEFAULT_PLATFORM_MAPPINGS: Record<string, Record<PlatformId, string>> = {
  // Sans-serif
  'SF Pro':         { ios: 'SF Pro',         macos: 'SF Pro',         android: 'Roboto',      windows: 'Segoe UI',    linux: 'Ubuntu' },
  'SF ProRounded':  { ios: 'SF Pro Rounded', macos: 'SF Pro Rounded', android: 'Roboto',      windows: 'Segoe UI',    linux: 'Ubuntu' },
  'Helvetica Neue': { ios: 'Helvetica Neue', macos: 'Helvetica Neue', android: 'Roboto',     windows: 'Arial',       linux: 'Liberation Sans' },
  'Avenir Next':    { ios: 'Avenir Next',    macos: 'Avenir Next',    android: 'Noto Sans',  windows: 'Calibri',     linux: 'Noto Sans' },
  // Serif
  'New York':       { ios: 'New York',       macos: 'New York',       android: 'Noto Serif',  windows: 'Georgia',     linux: 'DejaVu Serif' },
  'Georgia':        { ios: 'Georgia',        macos: 'Georgia',        android: 'Noto Serif',  windows: 'Georgia',     linux: 'DejaVu Serif' },
  'Baskerville':    { ios: 'Baskerville',    macos: 'Baskerville',    android: 'Noto Serif',  windows: 'Cambria',     linux: 'Liberation Serif' },
  // Monospace
  'SF Mono':        { ios: 'SF Mono',        macos: 'SF Mono',        android: 'Roboto Mono', windows: 'Consolas',    linux: 'Ubuntu Mono' },
};

/**
 * The hardcoded fallback chains that match the current (pre-configurable) behavior.
 * Used as the default when no fontConfig exists, ensuring backward compatibility.
 *
 * These MUST match the original FONT_FALLBACK_CHAINS from styleMapper.ts exactly.
 */
export const DEFAULT_FONT_FALLBACK_CHAINS: Record<string, string> = {
  // Sans-serif
  'SF Pro': "'SF Pro', 'Helvetica Neue', Helvetica, Arial, sans-serif",
  'SF ProRounded': "'SF ProRounded', 'SF Pro', 'Helvetica Neue', Helvetica, Arial, sans-serif",
  'Helvetica Neue': "'Helvetica Neue', Helvetica, Arial, sans-serif",
  'Avenir Next': "'Avenir Next', 'Avenir', 'Helvetica Neue', Helvetica, Arial, sans-serif",
  // Serif
  'New York': "'New York', 'Times New Roman', Georgia, serif",
  'Georgia': "'Georgia', 'Times New Roman', serif",
  'Baskerville': "'Baskerville', 'Palatino', 'Times New Roman', Georgia, serif",
  // Monospace
  'SF Mono': "'SF Mono', Consolas, 'Courier New', monospace",
};

/**
 * Find the category of a font within a platform's catalog.
 */
export function findFontCategory(
  platformId: PlatformId,
  fontName: string,
): FontCategory | null {
  const platform = SYSTEM_FONT_CATALOG[platformId];
  if (!platform) return null;

  for (const category of ['sans-serif', 'serif', 'monospace'] as FontCategory[]) {
    if (platform[category].some((f: SystemFontEntry) => f.name === fontName)) {
      return category;
    }
  }
  return null;
}

/**
 * Get all fonts for a platform, optionally filtered by category.
 */
export function getFontsForPlatform(
  platformId: PlatformId,
  category?: FontCategory,
): SystemFontEntry[] {
  const platform = SYSTEM_FONT_CATALOG[platformId];
  if (!platform) return [];

  if (category) {
    return platform[category] ?? [];
  }

  return [
    ...platform['sans-serif'],
    ...platform['serif'],
    ...platform['monospace'],
  ];
}

/**
 * Build CSS font-family fallback chains for all schema fonts,
 * given a font config and detected platform.
 *
 * When config is null or has no overrides for a font+platform,
 * falls back to DEFAULT_PLATFORM_MAPPINGS.
 *
 * @param config - Project font config from Firestore (or null)
 * @param platform - Detected platform ID
 * @returns Record mapping schema font names to CSS font-family strings
 */
export function buildFallbackChains(
  config: ProjectFontConfig | null,
  platform: PlatformId,
): Record<string, string> {
  const result: Record<string, string> = {};

  for (const [schemaFont, defaultMapping] of Object.entries(DEFAULT_PLATFORM_MAPPINGS)) {
    const targetFont = config?.platformMappings?.[schemaFont]?.[platform]
      ?? defaultMapping[platform];

    // Self-hosted web font: platform-independent family + metric-adjusted
    // fallback face (loaded by ensureWebFontsLoaded / the published-HTML bake).
    const webFont = isWebFontId(targetFont, config) ? getWebFont(targetFont, config) : null;
    if (webFont) {
      result[schemaFont] =
        `'${webFont.family}', '${webFontFallbackFamily(webFont.family)}', ${webFont.category}`;
      continue;
    }

    const category = findFontCategory(platform, targetFont) ?? 'sans-serif';

    result[schemaFont] = `'${targetFont}', ${category}`;
  }

  return result;
}
